import React, { useCallback, useMemo, useState } from 'react';
import { LayoutCtx } from 'shared/state/layoutContext';
import type { LayoutApi } from 'shared/state/layoutContext';

// Compatibility shim: LayoutApi, the context object and useLayout now live in
// shared/state/layoutContext so a feature (e.g. readiness) can call useLayout
// without importing app/. Every existing app-side import of this module keeps
// working through these re-exports.
export type { LayoutApi } from 'shared/state/layoutContext';
export { useLayout } from 'shared/state/layoutContext';

const SPINE_PINNED_KEY = 'v5000.v2.spinePinned';

// A per-user convenience, nothing more: storage can be missing or throw (private
// window, blocked site data), and the shell has to work exactly the same without it.
function readSpinePinned(): boolean {
  try { return window.localStorage.getItem(SPINE_PINNED_KEY) === 'true'; } catch { return false; }
}
function writeSpinePinned(pinned: boolean): void {
  try { window.localStorage.setItem(SPINE_PINNED_KEY, String(pinned)); } catch { /* not remembered; still pinned for this visit */ }
}

// tokens.css sets `scroll-behavior: auto !important`, but that is only consulted
// when the caller asks for `behavior: "auto"` (CSSOM-View) — an explicit "smooth"
// would bypass it, so a prefers-reduced-motion user would still get an animated
// scroll on every spine click. Read the media query directly instead. The
// optional call guards jsdom environments that never define matchMedia.
function scrollBehavior(): ScrollBehavior {
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  return reduceMotion ? 'auto' : 'smooth';
}

/** The shell's service (guidelines §2): features call this, never the shell's DOM. */
export function LayoutProvider({ children }: { children: React.ReactNode }) {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [spinePinned, setSpinePinned] = useState(readSpinePinned);
  const [overlay, setOverlay] = useState<string | null>(null);

  const jumpTo = useCallback((id: string) => {
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    setActiveSection(id);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: scrollBehavior() });
    setActiveSection(null);
  }, []);

  const toggleRail = useCallback(() => setRailCollapsed((v) => !v), []);
  const closeOverlay = useCallback(() => setOverlay(null), []);
  // The write sits beside the state change, not inside the updater: React may
  // run an updater twice (StrictMode), and a storage write is a side effect.
  const toggleSpinePin = useCallback(() => {
    const next = !spinePinned;
    setSpinePinned(next);
    writeSpinePinned(next);
  }, [spinePinned]);

  const value = useMemo<LayoutApi>(() => ({
    activeSection, setActiveSection, jumpTo, scrollToTop,
    railCollapsed, toggleRail,
    spinePinned, toggleSpinePin,
    overlay, openOverlay: setOverlay, closeOverlay,
  }), [activeSection, jumpTo, scrollToTop, railCollapsed, toggleRail, spinePinned, toggleSpinePin, overlay, closeOverlay]);

  return <LayoutCtx.Provider value={value}>{children}</LayoutCtx.Provider>;
}
